import { useState } from "react";
import {
	Box,
	Button,
	ButtonGroup,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { Close, Download } from "@mui/icons-material";
import type { MissionCSV } from "@rogoag/shared-ts";

export interface CsvDryRunDialogProps {
	open: boolean;
	onClose: () => void;
	csvs: MissionCSV[];
	onDownload: () => void;
}

export function CsvDryRunDialog({ open, onClose, csvs, onDownload }: CsvDryRunDialogProps) {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const csv = csvs[selectedIndex];
	const headers = csv?.headers ?? [];
	const rows = csv?.rows ?? [];

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper" disableScrollLock>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
					CSV Dry-Run
					{csvs.length > 1 && (
						<ButtonGroup size="small">
							{csvs.map((c, i) => (
								<Button
									key={c.filename}
									variant={i === selectedIndex ? "contained" : "outlined"}
									onClick={() => setSelectedIndex(i)}
								>
									{c.filename}
								</Button>
							))}
						</ButtonGroup>
					)}
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<IconButton onClick={onDownload} size="small">
						<Download />
					</IconButton>
					<IconButton onClick={onClose} size="small">
						<Close />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent dividers>
				{csv && (
					<>
						<Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
							{csv.filename} — {rows.length} rows
							{csv.errors.length > 0 && (
								<Typography component="span" variant="caption" color="error">
									{" "}
									({csv.errors.length} errors)
								</Typography>
							)}
						</Typography>
						<TableContainer>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										{headers.map((h, i) => (
											<TableCell key={i} sx={{ whiteSpace: "nowrap" }}>
												{h}
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map((row, ri) => {
										const hasErrors = row.errors.length > 0;
										const cells = row.toStringArray();
										const tableRow = (
											<TableRow key={ri} sx={hasErrors ? { bgcolor: "rgba(211, 47, 47, 0.06)" } : undefined}>
												{headers.map((_, ci) => (
													<TableCell
														key={ci}
														sx={{
															whiteSpace: "nowrap",
															color: hasErrors ? "error.main" : undefined,
														}}
													>
														{cells[ci] || ""}
													</TableCell>
												))}
											</TableRow>
										);
										if (hasErrors) {
											return (
												<Tooltip key={ri} title={row.errors.join("\n")} placement="left" arrow>
													{tableRow}
												</Tooltip>
											);
										}
										return tableRow;
									})}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
